<template lang="pug">
  div(class="payment-form")
    div(class="payment-form__image mb-4")
      v-img(
        v-if="!xsDevice"
        :src="require('~/assets/images/lightning.gif')"
        max-width="80"
      )
    div(class="payment-form__content")
      h2(
        class="fw-700 pb-2"
        :class="[xsDevice ? 'title-24px' : 'title-32px']"
      ) {{ title }}
      div(class="text-14px fw-300") {{ subtitle }}
      billing-payment-info(
        :info="paymentInfo"
        :loading="loading"
        gray
        class="my-8"
      )
    div(class="payment-form__actions")
      v-btn(
        class="main-button payment-form__actions--full"
        :loading="cardLoading"
        :disabled="loading"
        @click="handleCardPayment"
      ) {{ $t('Card payment') }}
      v-btn(
        class="secondary-button"
        :class="{ 'payment-form__actions--full': !showPayBalanceBtn }"
        :loading="invoiceLoading"
        :disabled="loading"
        @click="payByInvoice"
      ) {{ $t('Invoice for payment') }}
      v-btn(
        v-if="showPayBalanceBtn"
        class="secondary-button"
        :loading="balanceLoading"
        @click="withdrawBalance"
      ) {{ $t('Pay from balance') }}
</template>

<script>
import { mapGetters } from 'vuex'
import BillingPaymentInfo from '~/modules/billing/components/billing-payment-info.vue'
import { PLAN_TYPES } from '~/modules/billing/constants/common'
import currencyFormat from '~/mixins/filters/currencyFormat'
import actions from '~/modules/billing/mixins/actions'
import contentDialog from '~/mixins/dialogs/contentDialog'
import responsive from '~/mixins/pages/responsive'
import { isSafari } from '~/services/_utils/utils'

export default {
  name: 'BillingPaymentForm',
  components: { BillingPaymentInfo },
  mixins: [actions, currencyFormat, contentDialog, responsive],
  props: {
    closeModal: {
      type: Function,
      default: null
    },
    type: {
      type: String,
      validator: value => ['tariff', 'document'].includes(value),
      required: true
    },
    invoiceId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      invoiceLoading: false,
      invoice: null
    }
  },
  computed: {
    ...mapGetters('billing', {
      balance: 'balance'
    }),
    isPlanPayment() {
      return this.type === PLAN_TYPES.tariff
    },
    isDocumentPayment() {
      return this.type === PLAN_TYPES.document
    },
    title() {
      return this.isPlanPayment ? this.$t('Plan payment') : this.$t('Documents payment')
    },
    subtitle() {
      return this.isPlanPayment
        ? this.$t(
            'Choose a convenient payment method and save all your progress and settings in your workflow'
          )
        : this.$t('Instantly top up your document limit and don`t waste time waiting')
    },
    paymentInfo() {
      if (this.isPlanPayment) {
        return {
          firstRow: {
            label: this.$t('Premium plan for {months} mon', { months: 1 }), // todo: when more than monthly payment added
            value: this.handleUahString(this._.get(this.invoice, 'items[0].price', 0) / 100)
          },
          summaryRow: {
            label: `${this.$t('To be paid')}:`,
            value: this.handleUahString(this._.get(this.invoice, 'totalSum', 0) / 100)
          }
        }
      } else if (this.isDocumentPayment) {
        return {
          firstRow: {
            label: this.$t('Count'),
            value: `${this._.get(this.invoice, 'items[0].quantity', null)} док.`
          },
          secondRow: {
            label: this.$t('Price per document'),
            value: this.handleUahString(this._.get(this.invoice, 'items[0].price', 0) / 100)
          },
          summaryRow: {
            label: `${this.$t('To be paid')}:`,
            value: this.handleUahString(this._.get(this.invoice, 'totalSum', 0) / 100)
          }
        }
      }
      return null
    },
    showPayBalanceBtn() {
      return this.balance > this._.get(this.invoice, 'totalSum', 0)
    }
  },
  created() {
    this.getInvoiceInfo()
  },
  methods: {
    async getInvoiceInfo() {
      try {
        this.loading = true
        this.invoice = await this.$store.dispatch('invoices/read', this.invoiceId)
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    async payByInvoice() {
      try {
        this.invoiceLoading = true
        if (isSafari) {
          const paymentWindow = window.open('', '_blank')
          const blob = await this.$store.dispatch('invoices/payByBank', this.invoiceId)
          const url = URL.createObjectURL(blob)
          if (paymentWindow) {
            paymentWindow.location.href = url
          } else {
            window.location.href = url
          }
        } else {
          const blob = await this.$store.dispatch('invoices/payByBank', this.invoiceId)
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank')
        }
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.invoiceLoading = false
      }
    },
    async handleCardPayment() {
      await this.payByCard(this.invoiceId)
      this.closeModal()
    },
    async withdrawBalance() {
      await this.payByBalance(this.invoiceId, this.isPlanPayment)
      this.closeModal()
    }
  }
}
</script>

<style scoped lang="scss">
.payment-form {
  &__image {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    &--full {
      grid-column: span 2;
    }
  }
}
</style>
